import {
  Box,
  Button,
  CircularProgress,
  Modal,
  ThemeProvider,
  Typography,
} from "@material-ui/core";
import CloudDownloadOutlinedIcon from "@material-ui/icons/CloudDownloadOutlined";
import { useIntl } from "../../../../_metronic/i18n/customUseIntl";
import dsmTheme from "../../../modules/MUITheme";
import type { AppNotification } from "../../models/Notification/notificationTypes";
import { notificationModalStyles } from "../../../ThemeProvider";
import { useNotificationDownload } from "../../hooks/notifications/useNotificationDownload";
import sanitizeHtml from "../../utils/sanitize";
import { getAttachmentDisplayName } from "../../utils/string-utils";

type NotificationModalProps = {
  open: boolean;
  notifications: AppNotification[];
  onClose: () => void;
};

const NotificationModal = ({
  open,
  notifications,
  onClose,
}: NotificationModalProps) => {
  const intl = useIntl();
  const classes = notificationModalStyles();
  const { handleDownload, isLoadingKey, downloadError } =
    useNotificationDownload();

  return (
    <ThemeProvider theme={dsmTheme}>
      <Modal
        open={open}
        aria-labelledby="notification-modal-title"
        aria-describedby="notification-modal-description"
        className={classes.root}
      >
        <Box className={classes.modal}>
          <Box className={classes.header}>
            <Typography
              id="notification-modal-title"
              style={{ color: "white" }}
              variant="h5"
            >
              {intl.formatMessage({ id: "NOTIFICATIONS.USER" })}
            </Typography>
          </Box>

          <Box className={classes.notificationsContainer}>
            {notifications.map((notification) => (
              <Box key={notification.id} className={classes.modalBody}>
                <Typography variant="h6">{notification.title}</Typography>
                <Box
                  dangerouslySetInnerHTML={{
                    __html: sanitizeHtml(notification.message),
                  }}
                />

                {downloadError && (
                  <Typography
                    variant="body2"
                    color="error"
                    style={{ marginTop: 4 }}
                  >
                    {intl.formatMessage({ id: downloadError })}
                  </Typography>
                )}

                {notification.attachmentKeys &&
                  notification.attachmentKeys.length > 0 && (
                    <>
                      <Typography variant="body1">
                        <CloudDownloadOutlinedIcon
                          fontSize="large"
                          style={{ paddingBottom: "5px" }}
                        />
                        <strong>
                          {` ${intl.formatMessage({
                            id: "GENERAL.ATTACHEMENTS",
                          })}`}
                        </strong>
                      </Typography>
                      <ul className={classes.attachmentList}>
                        {notification.attachmentKeys.map((key) => (
                          <li key={key} className={classes.attachmentItem}>
                            <Button
                              variant="text"
                              size="small"
                              onClick={() => handleDownload(key)}
                              disabled={isLoadingKey(key)}
                              startIcon={
                                isLoadingKey(key) && (
                                  <CircularProgress size={16} />
                                )
                              }
                              style={{ color: "black", textTransform: "none" }}
                            >
                              {getAttachmentDisplayName(key)}
                            </Button>
                          </li>
                        ))}
                      </ul>
                    </>
                  )}
              </Box>
            ))}
          </Box>

          <Box className={classes.buttonContainer}>
            <Button
              variant="contained"
              color="secondary"
              fullWidth={false}
              onClick={onClose}
            >
              {intl.formatMessage({ id: "GENERAL.CLOSE" })}
            </Button>
          </Box>
        </Box>
      </Modal>
    </ThemeProvider>
  );
};

export default NotificationModal;
